import React from 'react';
import { Trophy, Star } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
interface RepData {
  id: string;
  name: string;
  avatar: string;
  rank: number;
  points: number;
  totalCalls: number;
}
function RankBadge({
  rank
}: {
  rank: number;
}) {
  if (rank === 1) {
    return <div className="relative" data-id="element-529">
        <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg" data-id="element-530">
          <span className="text-white font-black text-lg" data-id="element-531">1</span>
        </div>
        <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-xl" data-id="element-532">
          👑
        </div>
      </div>;
  }
  if (rank === 2) {
    return <div className="relative" data-id="element-533">
        <div className="w-12 h-12 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center shadow-lg" data-id="element-534">
          <span className="text-white font-black text-lg" data-id="element-535">2</span>
        </div>
        <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-xl" data-id="element-536">
          👑
        </div>
      </div>;
  }
  if (rank === 3) {
    return <div className="relative" data-id="element-537">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-lg" data-id="element-538">
          <span className="text-white font-black text-lg" data-id="element-539">3</span>
        </div>
        <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-xl" data-id="element-540">
          👑
        </div>
      </div>;
  }
  return <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center" data-id="element-541">
      <span className="text-gray-700 font-bold text-base" data-id="element-542">{rank}</span>
    </div>;
}
export function TopCzarPage() {
  const {
    currentUser
  } = useUser();
  // Get all users from localStorage and sort by average score
  const getAllUsers = (): RepData[] => {
    const stored = localStorage.getItem('usersData');
    if (!stored) return [];
    const users = JSON.parse(stored);
    return users.map((user: any, index: number) => ({
      id: user.id,
      name: user.name,
      avatar: user.avatar,
      rank: 0,
      points: user.averageScore,
      totalCalls: user.totalCalls
    })).sort((a: RepData, b: RepData) => b.points - a.points).map((user: RepData, index: number) => {
      const rank = index + 1;
      // Update the user's topRepRank in localStorage
      const usersData = JSON.parse(localStorage.getItem('usersData') || '[]');
      const userIndex = usersData.findIndex((u: any) => u.id === user.id);
      if (userIndex !== -1) {
        usersData[userIndex].topRepRank = rank <= 3 ? rank : null;
        localStorage.setItem('usersData', JSON.stringify(usersData));
      }
      return {
        ...user,
        rank
      };
    });
  };
  const allReps = getAllUsers();
  const topThree = allReps.slice(0, 3);
  const [first, second, third] = topThree;
  // If there aren't enough users yet, show a message
  if (allReps.length === 0) {
    return <div className="w-full px-6 pb-6 pt-4" data-id="element-543">
        <div className="mb-6" data-id="element-544">
          <h2 className="text-3xl font-black text-gray-900" data-id="element-545">Top Czars</h2>
          <p className="text-gray-500 text-sm mt-1" data-id="element-546">
            Leaderboard rankings by performance
          </p>
        </div>
        <div className="text-center py-12" data-id="element-547">
          <p className="text-gray-500" data-id="element-548">
            No data available yet. Start making calls to see rankings!
          </p>
        </div>
      </div>;
  }
  return <div className="w-full px-6 pb-6 pt-4" data-id="element-549">
      {/* Header */}
      <div className="mb-8" data-id="element-550">
        <h2 className="text-3xl font-black text-gray-900" data-id="element-551">Top Czars</h2>
        <p className="text-gray-500 text-sm mt-1" data-id="element-552">
          Leaderboard rankings by performance
        </p>
      </div>

      {/* Podium Section - More Compact */}
      <div className="relative mb-0 pt-8" data-id="element-553">
        {/* Decorative Stars */}
        <Star className="absolute top-12 left-8 w-4 h-4 text-yellow-400 fill-yellow-400 animate-pulse" style={{
        animationDelay: '0s'
      }} data-id="element-554" />
        <Star className="absolute top-20 right-12 w-3 h-3 text-yellow-400 fill-yellow-400 animate-pulse" style={{
        animationDelay: '0.5s'
      }} data-id="element-555" />
        <Star className="absolute top-32 left-16 w-3 h-3 text-yellow-400 fill-yellow-400 animate-pulse" style={{
        animationDelay: '1s'
      }} data-id="element-556" />
        <Star className="absolute top-28 right-20 w-4 h-4 text-yellow-400 fill-yellow-400 animate-pulse" style={{
        animationDelay: '1.5s'
      }} data-id="element-557" />

        {/* Top 3 Profiles - Smaller */}
        {topThree.length >= 3 && <>
            <div className="flex items-end justify-center gap-3 mb-8 relative z-10 pt-4" data-id="element-558">
              {/* 2nd Place - Left */}
              <div className="flex flex-col items-center" data-id="element-559">
                <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-gray-400 shadow-lg mb-2" data-id="element-560">
                  <img src={second.avatar} alt={second.name} className="w-full h-full object-cover" data-id="element-561" />
                </div>
                <p className="font-bold text-gray-900 text-xs mb-2" data-id="element-562">
                  {second.name}
                </p>
              </div>

              {/* 1st Place - Center (elevated) */}
              <div className="flex flex-col items-center -mt-6" data-id="element-563">
                <div className="relative mb-2" data-id="element-564">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-50" data-id="element-565">
                    <Trophy className="w-7 h-7 text-yellow-500 fill-yellow-400 drop-shadow-lg" data-id="element-566" />
                  </div>
                  <div className="w-20 h-20 rounded-full overflow-hidden border-3 border-yellow-400 shadow-xl relative z-20" data-id="element-567">
                    <img src={first.avatar} alt={first.name} className="w-full h-full object-cover" data-id="element-568" />
                  </div>
                </div>
                <p className="font-bold text-gray-900 text-sm mb-2" data-id="element-569">
                  {first.name}
                </p>
              </div>

              {/* 3rd Place - Right */}
              <div className="flex flex-col items-center" data-id="element-570">
                <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-blue-500 shadow-lg mb-2" data-id="element-571">
                  <img src={third.avatar} alt={third.name} className="w-full h-full object-cover" data-id="element-572" />
                </div>
                <p className="font-bold text-gray-900 text-xs mb-2" data-id="element-573">
                  {third.name}
                </p>
              </div>
            </div>

            {/* Podium Blocks - Smaller */}
            <div className="flex items-end justify-center gap-0 relative z-0" data-id="element-574">
              {/* 2nd Place Block - Silver */}
              <div className="flex-1 max-w-[110px] bg-gradient-to-br from-gray-300 to-gray-400 rounded-tl-2xl h-24 flex items-center justify-center" data-id="element-575">
                <div className="w-14 h-14 bg-gradient-to-br from-gray-300 to-gray-500 rounded-full flex items-center justify-center shadow-lg border-3 border-white" data-id="element-576">
                  <span className="text-white font-black text-xl" data-id="element-577">02</span>
                </div>
              </div>

              {/* 1st Place Block - Gold */}
              <div className="flex-1 max-w-[120px] bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-t-2xl h-32 flex flex-col items-center justify-center relative pt-6" data-id="element-578">
                <div className="absolute -top-4 text-2xl z-30" data-id="element-579">👑</div>
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg border-3 border-white" data-id="element-580">
                  <span className="text-white font-black text-2xl" data-id="element-581">01</span>
                </div>
              </div>

              {/* 3rd Place Block - Blue */}
              <div className="flex-1 max-w-[110px] bg-gradient-to-br from-blue-500 to-blue-600 rounded-tr-2xl h-20 flex items-center justify-center" data-id="element-582">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-lg border-3 border-white" data-id="element-583">
                  <span className="text-white font-black text-xl" data-id="element-584">03</span>
                </div>
              </div>
            </div>
          </>}
      </div>

      {/* Leaderboard Table - Connected to Podium */}
      <div className="relative z-10" data-id="element-585">
        <div className="bg-white rounded-b-2xl shadow-xl overflow-hidden" data-id="element-586">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-5 py-4 bg-gray-50 border-b border-gray-200" data-id="element-587">
            <div className="col-span-5 text-xs font-bold text-gray-500 uppercase tracking-wide" data-id="element-588">
              Reps
            </div>
            <div className="col-span-2 text-xs font-bold text-gray-500 uppercase tracking-wide text-center" data-id="element-589">
              Rank
            </div>
            <div className="col-span-3 text-xs font-bold text-gray-500 uppercase tracking-wide text-right" data-id="element-590">
              Calls
            </div>
            <div className="col-span-2 text-xs font-bold text-gray-500 uppercase tracking-wide text-right" data-id="element-591">
              Points
            </div>
          </div>

          {/* Table Rows */}
          <div className="divide-y divide-gray-100" data-id="element-592">
            {allReps.map((rep, index) => <div key={rep.id} className={`grid grid-cols-12 gap-4 px-5 py-4 items-center transition-colors ${index < 3 ? 'bg-gradient-to-r from-yellow-50/50 to-transparent' : rep.id === currentUser?.id ? 'bg-blue-50/30' : 'hover:bg-gray-50'}`} data-id="element-593">
                {/* Player */}
                <div className="col-span-5 flex items-center gap-3" data-id="element-594">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-200 flex-shrink-0" data-id="element-595">
                    <img src={rep.avatar} alt={rep.name} className="w-full h-full object-cover" data-id="element-596" />
                  </div>
                  <span className={`font-semibold truncate ${rep.id === currentUser?.id ? 'text-blue-600' : 'text-gray-900'}`} data-id="element-597">
                    {rep.name}
                    {rep.id === currentUser?.id && ' (You)'}
                  </span>
                </div>

                {/* Rank */}
                <div className="col-span-2 flex justify-center" data-id="element-598">
                  <RankBadge rank={rep.rank} data-id="element-599" />
                </div>

                {/* Total Calls */}
                <div className="col-span-3 text-right" data-id="element-600">
                  <span className="font-bold text-gray-900" data-id="element-601">
                    {rep.totalCalls}
                  </span>
                </div>

                {/* Points */}
                <div className="col-span-2 text-right" data-id="element-602">
                  <span className="font-bold text-green-600" data-id="element-603">
                    {rep.points.toFixed(1)}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}